import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	findNavbarBranchItemInCache,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { graphql } from "@src/gql/gql";
import { NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const navbarFavouriteMutation = graphql(/* GraphQL */ `
	mutation navbarFavourite(
		$section: NavbarSection!
		$itemKeys: [String!]!
		$waitForProjections: Boolean!
	) {
		navbar {
			favourite(
				section: $section
				itemKeys: $itemKeys
				waitForProjections: $waitForProjections
			)
		}
	}
`);

type NavbarFavouriteInput = {
	section: NavbarSection;
	itemKey?: string;
	itemKeys?: string[];
	waitForProjections?: boolean;
};

export function useNavbarFavourite() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			itemKey,
			itemKeys,
			waitForProjections,
		}: NavbarFavouriteInput) => {
			const keys = itemKeys ?? (itemKey ? [itemKey] : []);
			if (keys.length === 0) {
				return false;
			}

			const response = await client.mutate({
				mutation: navbarFavouriteMutation,
				variables: {
					section,
					itemKeys: keys,
					waitForProjections: waitForProjections ?? false,
				},
			});
			return response.data?.navbar?.favourite ?? false;
		},
		onMutate: async ({ section, itemKey, itemKeys }) => {
			const keys = itemKeys ?? (itemKey ? [itemKey] : []);
			if (keys.length === 0) {
				return;
			}

			const favQueryKey = navbarBranchQueryKeys.branch(
				NavbarSection.Favourites,
				null,
			);
			await queryClient.cancelQueries({ queryKey: favQueryKey });

			const previousFavourites =
				queryClient.getQueryData<NavbarBranchItem[]>(favQueryKey);

			const existingItems = keys
				.map((key) => findNavbarBranchItemInCache(queryClient, section, key))
				.filter((item): item is NavbarBranchItem => item != null);

			if (existingItems.length > 0) {
				queryClient.setQueryData<NavbarBranchItem[]>(favQueryKey, (old) => {
					const current = old ?? [];
					const existingKeys = new Set(current.map((i) => i.key));
					const itemsToAdd = existingItems.filter(
						(item) => !existingKeys.has(item.key),
					);

					if (itemsToAdd.length === 0) {
						return current;
					}

					return [...itemsToAdd, ...current];
				});
			}

			return { previousFavourites, favQueryKey };
		},
		onError: (error, _variables, context) => {
			if (context?.favQueryKey) {
				queryClient.setQueryData(
					context.favQueryKey,
					context.previousFavourites,
				);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, _vars, context) => {
			if (context?.favQueryKey) {
				queryClient.invalidateQueries({ queryKey: context.favQueryKey });
			}
		},
	});
}
